import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import { Flex, VStack, useDisclosure } from "@chakra-ui/react"

import { useShopifyVariants } from "@app/hooks/useShopify"
import { ProductContentTitle } from "@app/components/Product/Content/ProductContentTitle"
import { ProductStock } from "@app/components/Product/Stock/ProductStock"
import { ProductFormAddToCart } from "@app/components/Product/Form//ProductFormAddToCart"
import { ProductFormGrouped } from "@app/components/Product/Form//ProductFormGrouped"
import { ProductFormPrice } from "@app/components/Product/Form//ProductFormPrice"
import { ProductFormSwatches } from "@app/components/Product/Form//ProductFormSwatches"
import { ProductFormVariants } from "@app/components/Product/Form//ProductFormVariants"
import { ProductFormWishlist } from "@app/components/Product/Form//ProductFormWishlist"
import { ProductFormQuantity } from "@app/components/Product/Form/ProductFormQuantity"
import { PDPCallout } from "@app/components/PDPCallout"
import { ProductContent } from "@app/components/Product/Content/ProductContent"
import { ProductPairings } from "@app/components/Product/Pairings/ProductPairings"

import { useInsideDataProduct } from "@app/hooks/useInsideData"
import { ReviewOverview } from "@app/components/Product/Review/ReviewOverview"
import type { ProductProps, ProductCombinedProps } from "@root/types/global"
import { ProductFormBackInStock } from "@app/components/Product/Form/ProductFormBackInStock"
import { SelectableOption } from "@app/components/Product/Form/ProductFormTypes"
import { useSwatch } from "@app/hooks/useSwatch"
import { ReviewProps } from "@app/components/Product/Product"
import { useOutOfStock } from "@app/hooks/useOutOfStock"

type Props = {
  grouped?: Array<ProductProps>
  swatches?: Array<ProductProps>
  isLoading?: boolean
  product: ProductCombinedProps
  pairings?: Array<ProductProps>
  scrollToReview: () => void
  template?: ReviewProps
}

export const ProductForm: React.FC<Props> = memo(
  ({ grouped = [], swatches = [], isLoading = false, product, pairings, scrollToReview, template }) => {
    const [quantity, setQuantity] = useState<number>(1)
    const { isOpen: backInStockisOpen, onClose: backInStockonClose, onOpen: handleBackInStock } = useDisclosure()
    const [errors, setErrors] = useState(false)
    const { formatActiveProduct } = useSwatch()
    const { handleOption, selectedOptions, activeVariant } = useShopifyVariants({
      firstAvailable: product?.variants?.length === 1,
      loading: isLoading,
      product,
      useParameter: true,
    })

    const { getStockStatus } = useOutOfStock()

    const { isInStoreOnly } = getStockStatus(product, false)

    const selectableOptions: SelectableOption[] = useMemo(
      () =>
        product.options
          .filter(({ values }) => values?.length > 1)
          .map(({ name, values }) => ({
            name,
            values: values?.map(value => ({
              variant: product.variants.find(
                variant =>
                  variant?.selectedOptions?.filter(
                    selectedOption =>
                      selectedOption.value ===
                      (selectedOption.name === name ? value : selectedOptions.find(option => option.name === selectedOption.name)?.value)
                  )?.length === variant?.selectedOptions?.length
              ),
              value,
            })),
          })),
      [product.options, product.variants, selectedOptions]
    )

    useInsideDataProduct(product, activeVariant)

    const handleErrors = useCallback(() => setErrors(true), [setErrors])
    const handleQuantity = useCallback(quantity => (quantity > -1 ? setQuantity(quantity) : null), [setQuantity])

    const unselectedOptions = useMemo(() => selectedOptions.filter(({ value }) => !value), [selectedOptions])

    useEffect(() => {
      if (errors && !unselectedOptions.length) setErrors(false)
    }, [errors, setErrors, unselectedOptions.length])

    const formattedProduct = formatActiveProduct(product, activeVariant, true)

    return (
      <Flex
        flexDirection="column"
        alignItems="stretch"
        justifyContent="flex-start"
        maxW="full"
        px={[6.25, 0]}
        gap={6}
        position="sticky"
        top="200px"
        w={["full", 350, 432]}
      >
        <ProductFormBackInStock
          variant={activeVariant}
          isOpen={backInStockisOpen}
          onClose={backInStockonClose}
          product={product}
          formattedProduct={formattedProduct}
        />
        <Flex flexDirection="column" alignItems="stretch" justifyContent="flex-start" gap={6}>
          <VStack alignItems="stretch" justifyContent="flex-start" spacing={1}>
            <PDPCallout tags={product.tags} fontSize={12} letterSpacing="generous" padding={2} mb={3} lineHeight={1.25} />
            <Flex direction="row" alignItems="flex-start" justifyContent="space-between" gap={[4, 2, 6]}>
              <ProductContentTitle product={product} />
              <ProductFormPrice
                flexShrink={0}
                isLoading={isLoading}
                justifyContent={"flex-end"}
                product={product}
                textProps={{
                  fontSize: "1.5rem",
                  lineHeight: "32px",
                  fontWeight: "500",
                }}
                color="grey.text"
                w={"auto"}
                direction="column"
                alignItems="flex-end"
                spacing={[-1.5, -0.5]}
                quantity={quantity}
              />
            </Flex>
            <ReviewOverview visibility={template?.settingEnableReviews} productId={product.legacyId} scrollToFunction={scrollToReview} />
            <ProductFormSwatches isLoading={isLoading} product={product} swatches={swatches} />
          </VStack>

          {(grouped.length > 0 || selectableOptions.length > 0) && (
            <Flex flexDirection="column" alignItems="stretch" justifyContent="flex-start" gap={3.5}>
              <ProductFormGrouped grouped={grouped} product={product} />
              <ProductFormVariants
                handleChange={handleOption}
                isLoading={isLoading}
                product={product}
                unselectedOptions={errors ? unselectedOptions : undefined}
                selectedOptions={selectedOptions}
                selectableOptions={selectableOptions}
              />
            </Flex>
          )}
        </Flex>
        <VStack alignItems="stretch" justifyContent="flex-start" spacing={4}>
          <Flex gap={4}>
            <ProductFormQuantity value={quantity} handleChange={handleQuantity} max={activeVariant?.quantityAvailable} />
            <ProductFormAddToCart
              handleBackInStock={handleBackInStock}
              handleErrors={handleErrors}
              isLoading={isLoading}
              quantity={quantity}
              product={product}
            />
          </Flex>

          <>
            <Flex gap={4}>
              <ProductStock handleChange={handleOption} handleErrors={handleErrors} product={product} selectedOptions={selectedOptions} />
              {!isInStoreOnly && <ProductFormWishlist product={product} />}
            </Flex>
          </>
        </VStack>
        <ProductContent product={product} />
        <ProductPairings pairings={pairings} product={product} />
      </Flex>
    )
  }
)
